import React, { useState } from 'react';
import { customAuth } from '../../services/customAuth';
import { useDialog } from '../../context/DialogContext';
import { useLocale } from '../../context/LocaleContext';
import styles from './CustomLoginModal.module.css';

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({ isOpen, onClose, email }) => {
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const { showAlert } = useDialog();
  const { t } = useLocale();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError(t('auth.form.passwords_mismatch'));
      setIsLoading(false);
      return;
    }

    // Validate password strength
    if (newPassword.length < 8) {
      setError(t('auth.reset.password_min'));
      setIsLoading(false);
      return;
    }

    try {
      const result = await customAuth.resetPassword(email, resetCode, newPassword);
      if (result.success) {
        setSuccess(t('auth.reset.success'));
        showAlert({
          message: t('auth.reset.success'),
          variant: 'success'
        });
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.message || t('auth.reset.failed'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>{t('auth.reset.title')}</h2>
          <button onClick={onClose} className={styles.closeButton} aria-label={t('auth.form.close')}>×</button>
        </div>

        <div className={styles.verificationInfo}>
          <p>{t('auth.reset.code_sent_to')}</p>
          <p><strong>{email}</strong></p>
          <p>{t('auth.reset.check_email')}</p>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formGroup}>
            <label htmlFor="resetCode">{t('auth.reset.code_label')}</label>
            <input
              id="resetCode"
              type="text"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
              required
              placeholder={t('auth.reset.code_placeholder')}
              maxLength={6}
              className={styles.verificationInput}
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="newPassword">{t('auth.reset.new_password')}</label>
            <input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              placeholder={t('auth.reset.new_password_placeholder')}
            />
            <div className={styles.passwordHint}>
              {t('auth.reset.password_min')}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="confirmPassword">{t('auth.reset.confirm_new_password')}</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder={t('auth.reset.confirm_new_password_placeholder')}
            />
          </div>

          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}

          <button 
            type="submit" 
            className={styles.submitButton}
            disabled={isLoading}
          >
            {isLoading ? t('auth.reset.resetting') : t('auth.reset.submit')}
          </button>
        </form>

        <div className={styles.switchMode}>
          <button 
            type="button" 
            onClick={onClose}
            className={styles.switchButton}
          >
            {t('auth.form.back_to_sign_in')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetModal;
